import { ActionType, createReducer } from "typesafe-actions";

import { PermissionName } from "@mapmycustomers/shared";
import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import { OrganizationSize } from "@mapmycustomers/shared/types/OrganizationSize";
import Plan from "@mapmycustomers/shared/types/Plan";
import Sandbox from "@mapmycustomers/shared/types/Sandbox";
import ApiError from "@mapmycustomers/shared/util/api/ApiError";

import Iam from "@app/types/Iam";

import { initializeApp } from "../app/actions";
import { tryLogin } from "../auth/actions";

import {
  Actions,
  addFavoriteSavedFilter,
  addOrgFavoriteSavedFilter,
  changePassword,
  deleteProfilePhoto,
  fetchGeocodingLimit,
  fetchMe,
  fetchOrgSandbox,
  fetchPermissions,
  fetchPlans,
  fetchUsageLimits,
  findMe,
  getUserCountry,
  hideChangePasswordModal,
  hideDeleteProfilePhotoModal,
  removeFavoriteSavedFilters,
  removeOrgFavoriteSavedFilter,
  resetFlowStatuses,
  setOrganizationSize,
  setOrgSandbox,
  showChangePasswordModal,
  showDeleteProfilePhotoModal,
  togglePinnedDashboard,
  updateCustomFieldsUsage,
  updateFlowStatus,
  updateMe,
  updateMetadata,
  updateOrganization,
  updateOrganizationMetadata,
  updateOrganizationSetting,
  updateRecordUsage,
  updateSetting,
  uploadProfilePhoto,
} from "./actions";

export type IamState = {
  changePasswordError: ApiError | undefined;
  changePasswordLoading: boolean;
  changePasswordModalVisible: boolean;
  confirmDeleteProfileModalVisible: boolean;
  deleteProfilePhotoLoading: boolean;
  error: ApiError | undefined;
  finishOnboardingError: undefined | unknown;
  finishOnboardingLoading: boolean;
  geocodingMmcLimitReached: boolean;
  geocodingOrgLimit: number;
  geocodingOrgLimitReached: boolean;
  layouts: FormLayout[];
  loading: boolean;
  me: Iam | undefined;
  meUpdating: boolean;
  organizationSize: OrganizationSize;
  organizationUpdating: boolean;
  orgSandbox?: Sandbox;
  orgSandboxLoaded: boolean; // The sandbox was read or set in this session.
  permissionMap: Partial<Record<PermissionName, boolean>>;
  plans: Plan[];
  position: GeolocationPosition | undefined;
  settingUpdating: boolean;
  uploadProfilePhotoLoading: boolean;
  userCountry?: CountryCode;
};

const initialState: IamState = {
  changePasswordError: undefined,
  changePasswordLoading: false,
  changePasswordModalVisible: false,
  confirmDeleteProfileModalVisible: false,
  deleteProfilePhotoLoading: false,
  error: undefined,
  finishOnboardingError: undefined,
  finishOnboardingLoading: false,
  geocodingMmcLimitReached: false,
  geocodingOrgLimit: 0,
  geocodingOrgLimitReached: false,
  layouts: [],
  loading: false,
  me: undefined,
  meUpdating: false,
  organizationSize: "small",
  organizationUpdating: false,
  orgSandboxLoaded: false,
  permissionMap: {},
  plans: [],
  position: undefined,
  settingUpdating: false,
  uploadProfilePhotoLoading: false,
};

const iam = createReducer<
  IamState,
  Actions | ActionType<typeof initializeApp.request | typeof tryLogin>
>(initialState)
  .handleAction(tryLogin.request, (state) => ({
    ...state,
    // cleanup the error, which is useful when user is re-logging in because of the
    // expired token
    error: undefined,
  }))
  // a sign-in may be into another org; the initialize saga fetches the sandbox again
  .handleAction(initializeApp.request, (state) => ({
    ...state,
    orgSandbox: undefined,
    orgSandboxLoaded: false,
  }))
  .handleAction(fetchMe.request, (state) => ({
    ...state,
    error: undefined,
    loading: true,
    me: undefined,
  }))
  .handleAction(fetchMe.success, (state, action) => ({
    ...state,
    loading: false,
    me: action.payload,
  }))
  .handleAction([fetchOrgSandbox.success, setOrgSandbox], (state, { payload }) => ({
    ...state,
    orgSandbox: payload,
    orgSandboxLoaded: true,
  }))
  .handleAction(fetchPermissions.success, (state, { payload }) => ({
    ...state,
    permissionMap: payload.reduce(
      (result, permission) => {
        result[permission.name] = permission.isActive;
        return result;
      },
      {} as Record<PermissionName, boolean>
    ),
  }))
  .handleAction(fetchMe.failure, (state, { payload }) => ({
    ...state,
    error:
      payload instanceof ApiError
        ? payload
        : new ApiError("Unknown error, please try again later or contact support"),
    loading: false,
  }))
  .handleAction(findMe.success, (state, action) => ({
    ...state,
    position: action.payload,
  }))
  .handleAction(getUserCountry.success, (state, { payload }) => ({
    ...state,
    userCountry: payload,
  }))
  .handleAction(changePassword.request, (state) => ({
    ...state,
    changePasswordError: undefined,
    changePasswordLoading: true,
  }))
  .handleAction(changePassword.success, (state) => ({
    ...state,
    changePasswordLoading: false,
    changePasswordModalVisible: false,
  }))
  .handleAction(changePassword.failure, (state, { payload }) => ({
    ...state,
    changePasswordError:
      payload instanceof ApiError
        ? payload
        : new ApiError("Unknown error, please try again later or contact support"),
    changePasswordLoading: false,
  }))
  .handleAction(showChangePasswordModal, (state) => ({
    ...state,
    changePasswordModalVisible: true,
  }))
  .handleAction(hideChangePasswordModal, (state) => ({
    ...state,
    changePasswordModalVisible: false,
  }))
  .handleAction(uploadProfilePhoto.request, (state) => ({
    ...state,
    error: undefined,
    uploadProfilePhotoLoading: true,
  }))
  .handleAction(uploadProfilePhoto.success, (state, { payload }) => ({
    ...state,
    me: state.me ? { ...state.me, ...payload } : state.me,
    uploadProfilePhotoLoading: false,
  }))
  .handleAction(uploadProfilePhoto.failure, (state, { payload }) => ({
    ...state,
    error:
      payload instanceof ApiError
        ? payload
        : new ApiError("Unknown error, please try again later or contact support"),
    uploadProfilePhotoLoading: false,
  }))
  .handleAction(deleteProfilePhoto.request, (state) => ({
    ...state,
    deleteProfilePhotoLoading: true,
    error: undefined,
  }))
  .handleAction(deleteProfilePhoto.success, (state, { payload }) => ({
    ...state,
    confirmDeleteProfileModalVisible: false,
    deleteProfilePhotoLoading: false,
    me: state.me ? { ...state.me, ...payload } : state.me,
  }))
  .handleAction(deleteProfilePhoto.failure, (state, { payload }) => ({
    ...state,
    confirmDeleteProfileModalVisible: false,
    deleteProfilePhotoLoading: false,
    error:
      payload instanceof ApiError
        ? payload
        : new ApiError("Unknown error, please try again later or contact support"),
  }))
  .handleAction(hideDeleteProfilePhotoModal, (state) => ({
    ...state,
    confirmDeleteProfileModalVisible: false,
  }))
  .handleAction(showDeleteProfilePhotoModal, (state) => ({
    ...state,
    confirmDeleteProfileModalVisible: true,
  }))
  .handleAction(updateSetting.request, (state) => ({
    ...state,
    settingUpdating: true,
  }))
  .handleAction(updateSetting.success, (state, { payload }) => {
    const settingIndex = state.me?.settings?.findIndex(({ key }) => key === payload.key);
    if (!settingIndex || settingIndex < 0) {
      // if it's a new setting, add it to the list
      return {
        ...state,
        me: state.me ? { ...state.me, settings: [...state.me.settings, payload] } : state.me,
        settingUpdating: false,
      };
    } else {
      //else update existing
      return state.me
        ? {
            ...state,
            me: {
              ...state.me,
              settings: [
                ...state.me.settings.slice(0, settingIndex),
                payload,
                ...state.me.settings.slice(settingIndex + 1),
              ],
            },
            settingUpdating: false,
          }
        : { ...state, settingUpdating: false };
    }
  })
  .handleAction(
    [
      addFavoriteSavedFilter.success,
      removeFavoriteSavedFilters.success,
      togglePinnedDashboard.success,
    ],
    (state, { payload }) => ({
      ...state,
      me: state.me ? { ...state.me, ...payload } : state.me,
    })
  )
  .handleAction(
    [addOrgFavoriteSavedFilter.success, removeOrgFavoriteSavedFilter.success],
    (state, { payload }) => ({
      ...state,
      me: state.me
        ? { ...state.me, organization: { ...state.me.organization, ...payload } }
        : state.me,
    })
  )
  .handleAction(updateMe.request, (state) => ({
    ...state,
    meUpdating: true,
  }))
  .handleAction(updateMe.success, (state, { payload }) => ({
    ...state,
    me: payload,
    meUpdating: false,
  }))
  .handleAction(updateMe.failure, (state) => ({
    ...state,
    meUpdating: false,
  }))
  .handleAction(updateOrganization.request, (state) => ({
    ...state,
    organizationUpdating: true,
  }))
  .handleAction(
    [updateOrganization.success, updateOrganizationSetting.success],
    (state, { payload }) => ({
      ...state,
      me: state.me ? { ...state.me, ...payload } : state.me,
      organizationUpdating: false,
    })
  )
  .handleAction(updateOrganization.failure, (state) => ({
    ...state,
    organizationUpdating: false,
  }))
  .handleAction(
    fetchGeocodingLimit.success,
    (
      state,
      { payload: { geocodingMmcLimitReached, geocodingOrgLimit, geocodingOrgLimitReached } }
    ) => ({
      ...state,
      geocodingMmcLimitReached,
      geocodingOrgLimit,
      geocodingOrgLimitReached,
    })
  )
  .handleAction(updateMetadata.request, (state) => ({ ...state, meUpdating: true }))
  .handleAction(updateMetadata.success, (state, { payload }) => ({
    ...state,
    me: state.me ? { ...state.me, metaData: { ...state.me.metaData, ...payload } } : state.me,
    meUpdating: false,
  }))
  .handleAction(updateMetadata.failure, (state) => ({ ...state, meUpdating: false }))
  .handleAction([updateFlowStatus.success, resetFlowStatuses.success], (state, { payload }) => ({
    ...state,
    me: state.me ? { ...state.me, metaData: { ...state.me.metaData, ...payload } } : state.me,
  }))
  .handleAction(updateOrganizationMetadata.request, (state) => ({
    ...state,
    organizationUpdating: true,
  }))
  .handleAction(updateOrganizationMetadata.success, (state, { payload }) => ({
    ...state,
    me: state.me
      ? { ...state.me, organization: { ...state.me.organization, ...payload } }
      : state.me,
    organizationUpdating: false,
  }))
  .handleAction(updateOrganizationMetadata.failure, (state) => ({
    ...state,
    organizationUpdating: false,
  }))
  .handleAction(setOrganizationSize, (state, { payload }) => ({
    ...state,
    organizationSize: payload,
  }))
  .handleAction(fetchPlans.success, (state, { payload }) => ({
    ...state,
    plans: payload,
  }))
  .handleAction(fetchUsageLimits.success, (state, { payload }) => ({
    ...state,
    me: state.me ? { ...state.me, usageLimits: payload } : undefined,
  }))
  .handleAction(updateRecordUsage, (state, { payload }) => ({
    ...state,
    me: state.me
      ? {
          ...state.me,
          usageLimits: {
            ...state.me.usageLimits,
            records: {
              ...state.me.usageLimits.records,
              usage: Math.max(0, (state.me.usageLimits.records.usage ?? 0) + payload),
            },
          },
        }
      : undefined,
  }))
  .handleAction(updateCustomFieldsUsage, (state, { payload }) => ({
    ...state,
    me: state.me
      ? {
          ...state.me,
          usageLimits: {
            ...state.me.usageLimits,
            customFields: {
              ...state.me.usageLimits.customFields,
              usage: Math.max(0, (state.me.usageLimits.customFields.usage ?? 0) + payload),
            },
          },
        }
      : undefined,
  }));

export * from "./selectors";
export type IamActions = Actions;
export default iam;
